import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';
import { SITE_LOGO } from '../../constants/assets';
import AdminProducts from './components/AdminProducts';
import AdminBlog from './components/AdminBlog';
import AdminServices from './components/AdminServices';
import AdminTestimonials from './components/AdminTestimonials';
import AdminFAQ from './components/AdminFAQ';
import AdminContact from './components/AdminContact';
import AdminBookings from './components/AdminBookings';
import AdminOrders from './components/AdminOrders';
import AdminPortfolio from './components/AdminPortfolio';
import AdminAsesoriaPlans from './components/AdminAsesoriaPlans';
import AdminMenuVisibility from './components/AdminMenuVisibility';
import AdminMaintenance from './components/AdminMaintenance';
import AdminB2B from './components/AdminB2B';
import AdminEstimates from './components/AdminEstimates';

const LOGIN_KEY = 'admin_auth';
const ADMIN_PASSWORD = 'couture2025'; // Simple static auth for demo

type Tab = 'products' | 'blog' | 'services' | 'testimonials' | 'faq' | 'contact' | 'bookings' | 'orders' | 'portfolio' | 'asesoria' | 'visibility' | 'maintenance' | 'b2b' | 'estimates';

export default function AdminPage() {
  const [isAuth, setIsAuth] = useState(() => {
    try {
      return localStorage.getItem(LOGIN_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('products');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useSEO({
    title: 'Panel de Administración | Anthony Sánchez Couture',
    description: 'Gestión de contenido del atelier de alta costura.',
    robots: 'noindex, nofollow',
  });

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuth(true);
      try { localStorage.setItem(LOGIN_KEY, 'true'); } catch { /* ignore */ }
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleLogout = () => {
    setIsAuth(false);
    setPassword('');
    try { localStorage.removeItem(LOGIN_KEY); } catch { /* ignore */ }
  };

  const handleResetAll = () => {
    setResetLoading(true);
    const adminKeys = [
      'admin_products', 'admin_blog', 'admin_services', 'admin_steps',
      'admin_testimonials', 'admin_badges', 'admin_alta_costura_testimonials',
      'admin_wedding_faqs', 'admin_contact_info', 'admin_contact_faqs',
    ];
    adminKeys.forEach((key) => {
      try { localStorage.removeItem(key); } catch { /* ignore */ }
    });
    setTimeout(() => {
      setResetLoading(false);
      setResetSuccess(true);
      setTimeout(() => {
        setShowResetDialog(false);
        setResetSuccess(false);
        window.location.reload();
      }, 1500);
    }, 600);
  };

  if (!isAuth) {
    return (
      <div className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <img
              src={SITE_LOGO}
              alt="Anthony Sánchez Couture"
              className="h-14 w-auto object-contain mx-auto"
            />
            <h1 className="font-serif text-brand-black text-2xl font-medium mt-6">Panel de Administración</h1>
            <p className="font-sans text-brand-gray-500 text-sm mt-2">Acceso restringido para gestión de contenido</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white rounded-2xl p-8 border border-brand-gray-200 space-y-5">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(false); }}
                className={`mt-1.5 w-full border rounded-lg px-4 py-3 font-sans text-sm focus:outline-none transition-colors ${
                  error ? 'border-red-400 bg-red-50' : 'border-brand-gray-200 focus:border-brand-black'
                }`}
                placeholder="Introduce la contraseña"
                autoFocus
              />
              {error && <p className="font-sans text-red-500 text-xs mt-2">Contraseña incorrecta.</p>}
            </div>
            <button
              type="submit"
              className="w-full bg-brand-black text-white font-sans text-sm tracking-widest uppercase py-3.5 rounded-lg hover:bg-brand-gray-600 transition-colors"
            >
              Acceder
            </button>
            <Link to="/" className="block text-center font-sans text-brand-gray-500 text-sm hover:text-brand-black">
              Volver al sitio web
            </Link>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'products', label: 'Productos', icon: 'ri-shopping-bag-line' },
    { key: 'blog', label: 'Blog', icon: 'ri-article-line' },
    { key: 'services', label: 'Servicios', icon: 'ri-scissors-cut-line' },
    { key: 'b2b', label: 'Portal B2B', icon: 'ri-building-line' },
    { key: 'portfolio', label: 'Portafolio', icon: 'ri-gallery-line' },
    { key: 'testimonials', label: 'Testimonios', icon: 'ri-star-line' },
    { key: 'faq', label: 'FAQ', icon: 'ri-questionnaire-line' },
    { key: 'contact', label: 'Contacto', icon: 'ri-contacts-line' },
    { key: 'bookings', label: 'Reservas', icon: 'ri-calendar-line' },
    { key: 'orders', label: 'Pedidos', icon: 'ri-shopping-bag-line' },
    { key: 'estimates', label: 'Presupuestos', icon: 'ri-calculator-line' },
    { key: 'asesoria', label: 'Asesoría', icon: 'ri-user-star-line' },
    { key: 'visibility', label: 'Visibilidad', icon: 'ri-eye-line' },
    { key: 'maintenance', label: 'Mantenimiento', icon: 'ri-tools-line' },
  ];

  return (
    <div className="min-h-screen bg-brand-gray-50">
      <header className="bg-white border-b border-brand-gray-200 sticky top-0 z-40">
        <div className="px-6 lg:px-[4%] py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src={SITE_LOGO}
              alt="Anthony Sánchez Couture"
              className="h-10 w-auto object-contain"
            />
            <span className="hidden sm:inline font-sans text-brand-gray-400 text-sm">Panel de Administración</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-sans text-brand-gray-600 text-sm hover:text-brand-black">Ver web</Link>
            <button onClick={handleLogout} className="font-sans text-brand-gray-600 text-sm px-4 py-2 rounded-lg border border-brand-gray-200 hover:border-brand-black transition-colors">
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <div className="px-6 lg:px-[4%] py-6 overflow-x-auto">
        <div className="flex items-center gap-2 pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as Tab)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-brand-black text-white'
                  : 'bg-white border border-brand-gray-200 text-brand-gray-600 hover:text-brand-black'
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-6 lg:px-[4%] pb-20">
        {activeTab === 'products' && <AdminProducts />}
        {activeTab === 'blog' && <AdminBlog />}
        {activeTab === 'services' && <AdminServices />}
        {activeTab === 'b2b' && <AdminB2B />}
        {activeTab === 'portfolio' && <AdminPortfolio />}
        {activeTab === 'testimonials' && <AdminTestimonials />}
        {activeTab === 'faq' && <AdminFAQ />}
        {activeTab === 'contact' && <AdminContact />}
        {activeTab === 'bookings' && <AdminBookings />}
        {activeTab === 'orders' && <AdminOrders />}
        {activeTab === 'estimates' && <AdminEstimates />}
        {activeTab === 'asesoria' && <AdminAsesoriaPlans />}
        {activeTab === 'visibility' && <AdminMenuVisibility />}
        {activeTab === 'maintenance' && <AdminMaintenance />}
      </main>
    </div>
  );
}